'use client';

import { useState, type ReactNode } from 'react';
import { Mail, MessageCircle, Orbit, Phone, Send, Smartphone, Users } from 'lucide-react';

const FEATURES = [
  {
    icon: Mail,
    title: 'Direct Messaging',
    body: 'Send personalised messages directly to any profile.',
    quote: 'Do not hesitate! Send personalised messages directly to any profile you like.',
  },
  {
    icon: MessageCircle,
    title: '1-to-1 Member Chat',
    body: 'Real-time private chat within the app.',
    quote: 'A private conversation, only between the two of you.',
  },
  {
    icon: Smartphone,
    title: 'View Phone Numbers',
    body: "See contact numbers of profiles you're interested in.",
    quote: 'When both families are ready, their number is one tap away.',
  },
  {
    icon: Users,
    title: 'Expert Handpicked Matches',
    body: 'MVVS team personally selects best matches for you.',
    quote: 'Profiles chosen by our team, not by an endless feed.',
  },
  {
    icon: Orbit,
    title: 'Kundali Matching',
    body: 'Complete 36 Guna Milan & Ashtakoot Score Table',
    quote: 'See the full 36 Guna Milan before you take the next step.',
  },
] as const;

type Feature = (typeof FEATURES)[number];

function FeatureCard({
  feature,
  highlighted,
  onSelect,
}: {
  feature: Feature;
  highlighted: boolean;
  onSelect: () => void;
}) {
  const Icon = feature.icon;

  return (
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={highlighted}
        className={`flex w-full items-start gap-4 rounded-[18px] p-[21px] text-left md:px-[25px] ${
          highlighted
            ? 'bg-[#6C38FF] text-white shadow-[0_8px_24px_rgba(80,40,200,0.25)]'
            : 'bg-white text-black shadow-[0_8px_24px_rgba(0,0,0,0.06)]'
        }`}
      >
        <Icon
          className={`mt-0.5 h-6 w-6 shrink-0 ${highlighted ? 'text-white' : 'text-black'}`}
          strokeWidth={1.75}
          aria-hidden
        />
        <span className="min-w-0">
          <span className={`block text-[16px] font-semibold leading-snug md:text-[18px] ${highlighted ? 'text-white' : 'text-black'}`}>
            {feature.title}
          </span>
          <span className={`mt-[7px] block text-[14px] font-normal leading-snug ${highlighted ? 'text-white/90' : 'text-neutral-600'}`}>
            {feature.body}
          </span>
        </span>
      </button>
  );
}

function PreviewAction({ children }: { children: ReactNode }) {
  return (
    <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-[14px] font-semibold text-white">
      {children}
    </span>
  );
}

function PreviewBody({ feature }: { feature: Feature }) {
  if (feature.title === 'Direct Messaging') {
    return (
      <>
        <div className="mt-3 rounded-2xl bg-white px-4 py-3 text-[14px] leading-snug text-[#232323]">
          Hi Aditi, I saw your profile and think we&apos;d be a great match - would love to connect!
        </div>
        <PreviewAction>
          Send Message
          <Send size={16} aria-hidden />
        </PreviewAction>
      </>
    );
  }

  if (feature.title === '1-to-1 Member Chat') {
    return (
      <>
        <div className="mt-3 space-y-2">
          <div className="max-w-[88%] rounded-2xl rounded-bl-md bg-white px-4 py-2.5 text-[14px] leading-snug text-[#232323]">
            <p className="mb-0.5 text-[11px] text-neutral-400">Aditi</p>
            Namaskar, I liked your profile.
          </div>
          <div className="ml-auto max-w-[88%] rounded-2xl rounded-br-md bg-[#d9fdd3] px-4 py-2.5 text-[14px] leading-snug text-[#111b21]">
            Thank you. Shall we talk this evening?
          </div>
        </div>
        <PreviewAction>
          Open Chat
          <MessageCircle size={16} aria-hidden />
        </PreviewAction>
      </>
    );
  }

  if (feature.title === 'View Phone Numbers') {
    return (
      <>
        <div className="mt-3 rounded-2xl bg-white px-4 py-3">
          <p className="text-[12px] text-neutral-500">Aditi Shahane · Pune</p>
          <p className="mt-1 text-[18px] font-semibold leading-none text-[#232323]">+91 85914 07273</p>
        </div>
        <PreviewAction>
          Call Now
          <Phone size={16} aria-hidden />
        </PreviewAction>
      </>
    );
  }

  if (feature.title === 'Expert Handpicked Matches') {
    return (
      <>
        <div className="mt-3 space-y-2">
          {[
            ['Aditi', '27 yrs · Pune · Maratha', '98%'],
            ['Sneha', '26 yrs · Mumbai · Maratha', '95%'],
          ].map(([name, meta, score]) => (
            <div key={name} className="flex items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3">
              <span>
                <span className="block text-[14px] font-semibold text-[#232323]">{name}</span>
                <span className="mt-0.5 block text-[12px] text-neutral-500">{meta}</span>
              </span>
              <span className="shrink-0 rounded-full bg-[#fce8f1] px-2.5 py-1 text-[11px] font-semibold text-primary-600">
                {score}
              </span>
            </div>
          ))}
        </div>
        <PreviewAction>
          View Matches
          <Users size={16} aria-hidden />
        </PreviewAction>
      </>
    );
  }

  return (
    <>
      <div className="mt-3 rounded-2xl bg-white px-4 py-3">
        <div className="flex items-end justify-between">
          <p className="text-[13px] text-neutral-500">Guna Milan</p>
          <p className="text-[22px] font-semibold leading-none text-[#232323]">
            32<span className="text-[14px] font-normal text-neutral-500">/36</span>
          </p>
        </div>
        <div className="mt-3 space-y-1.5 text-[13px] text-[#232323]">
          {[
            ['Varna', '1/1'],
            ['Vashya', '2/2'],
            ['Tara', '3/3'],
            ['Yoni', '4/4'],
          ].map(([name, score]) => (
            <div key={name} className="flex items-center justify-between">
              <span>{name}</span>
              <span className="font-semibold">{score}</span>
            </div>
          ))}
        </div>
      </div>
      <PreviewAction>
        View Full Score
        <Orbit size={16} aria-hidden />
      </PreviewAction>
    </>
  );
}

function MessagePreview({ feature, tail }: { feature: Feature; tail: 'up' | 'down' }) {
  return (
    <div className="w-full">
      {tail === 'up' && (
        <span
          aria-hidden
          className="mx-auto block h-0 w-0 border-x-[16px] border-b-[14px] border-x-transparent border-b-[#f3e9ff]"
        />
      )}
      <div className="rounded-[28px] bg-[#f3e9ff] px-4 py-4 text-left md:px-5 md:py-5">
        <p className="text-[13px] text-neutral-500">{feature.title}</p>
        <PreviewBody feature={feature} />
      </div>
      {tail === 'down' && (
        <span
          aria-hidden
          className="mx-auto block h-0 w-0 border-x-[16px] border-t-[14px] border-x-transparent border-t-[#f3e9ff]"
        />
      )}
    </div>
  );
}

function Quote({ text }: { text: string }) {
  return (
    <p className="text-center text-[18px] font-medium italic leading-snug text-white md:text-[22px]">“{text}”</p>
  );
}

export default function HomePremiumSection() {
  const [selected, setSelected] = useState(0);
  const feature = FEATURES[selected];

  return (
    <section className="relative overflow-hidden py-16 font-sans text-white md:py-20">
      <div
        aria-hidden
        className="absolute inset-0 bg-[linear-gradient(180deg,#ED3D94_0%,#A50C45_100%)] lg:bg-[linear-gradient(90deg,#ED3D94_0%,#A50C45_100%)]"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-repeat"
        style={{ backgroundImage: "url('/assets/img/moroccan-bg.png')" }}
      />

      <div className="relative z-10 mx-auto w-full max-w-[1120px] px-5 md:px-8">
        <div className="text-center">
          <p className="text-[13px] font-semibold uppercase tracking-[2px]">Premium</p>
          <h2 className="mt-2 text-[28px] font-bold leading-tight md:text-[40px]">Unlock the full experience</h2>
        </div>

        <div className="mt-8 grid items-center gap-8 lg:mt-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-12">
          <ul className="flex flex-col gap-3">
            {FEATURES.map((item, index) => (
              <li key={item.title} className="flex flex-col gap-3">
                <FeatureCard feature={item} highlighted={index === selected} onSelect={() => setSelected(index)} />
                {index === selected && (
                  <div className="flex flex-col gap-4 py-2 lg:hidden">
                    <Quote text={feature.quote} />
                    <MessagePreview feature={feature} tail="up" />
                  </div>
                )}
              </li>
            ))}
          </ul>

          <div className="hidden flex-col items-center gap-6 lg:flex">
            <MessagePreview feature={feature} tail="down" />
            <Quote text={feature.quote} />
          </div>
        </div>
      </div>
    </section>
  );
}
